/**
 * Marker Component
 *
 * Renders a Google Maps marker at the specified location.
 * Migrated from recompose to React hooks.
 */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

function Marker({ google, map, icon, label, labelClass, location }) {
	const markerRef = useRef(null);

	useEffect(() => {
		if (!google || !map) {
			return undefined;
		}

		// Create marker
		const marker = new google.maps.Marker({
			position: new google.maps.LatLng(
				location.latitude,
				location.longitude,
			),
			map,
			icon,
			label,
			labelClass,
			zIndex: 100,
		});

		markerRef.current = marker;

		// Cleanup: remove marker from map on unmount
		return () => {
			if (markerRef.current) {
				markerRef.current.setMap(null);
			}
		};
	}, [google, map]); // Only create marker once when google and map are available

	// Update marker position when location changes
	useEffect(() => {
		if (!markerRef.current || !google || !location) return;

		const position = new google.maps.LatLng(
			location.latitude,
			location.longitude,
		);
		markerRef.current.setPosition(position);
	}, [location, google]);

	// This component doesn't render anything visible - it only manages the Google Maps marker
	return null;
}

Marker.propTypes = {
	google: PropTypes.object,
	map: PropTypes.object,
	icon: PropTypes.object,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	labelClass: PropTypes.string,
	location: PropTypes.shape({
		latitude: PropTypes.number.isRequired,
		longitude: PropTypes.number.isRequired,
	}).isRequired,
};

export default Marker;
